// @ts-check
/**
 * Countries offered wherever staff pick a phone or event country. Dial codes are seeded from
 * frontend-next/src/constants/countries.js (DIAL_CODES). India and the USA come first (the two
 * markets staff sell in most), then everything else alphabetically by name.
 */

/** @typedef {{iso: string, name: string, dial: string, flag: string}} Country */

/** ISO code -> [full name, dial code]. */
const SOURCE = {
  IN: ['India', '+91'],
  US: ['United States', '+1'],
  AE: ['United Arab Emirates', '+971'],
  AT: ['Austria', '+43'],
  AU: ['Australia', '+61'],
  BD: ['Bangladesh', '+880'],
  BE: ['Belgium', '+32'],
  BH: ['Bahrain', '+973'],
  BR: ['Brazil', '+55'],
  CA: ['Canada', '+1'],
  CH: ['Switzerland', '+41'],
  CN: ['China', '+86'],
  CZ: ['Czechia', '+420'],
  DE: ['Germany', '+49'],
  DK: ['Denmark', '+45'],
  EG: ['Egypt', '+20'],
  ES: ['Spain', '+34'],
  FI: ['Finland', '+358'],
  FR: ['France', '+33'],
  GB: ['United Kingdom', '+44'],
  GR: ['Greece', '+30'],
  HK: ['Hong Kong', '+852'],
  HU: ['Hungary', '+36'],
  ID: ['Indonesia', '+62'],
  IE: ['Ireland', '+353'],
  IT: ['Italy', '+39'],
  JP: ['Japan', '+81'],
  KR: ['South Korea', '+82'],
  KW: ['Kuwait', '+965'],
  LK: ['Sri Lanka', '+94'],
  LU: ['Luxembourg', '+352'],
  MX: ['Mexico', '+52'],
  MY: ['Malaysia', '+60'],
  NL: ['Netherlands', '+31'],
  NO: ['Norway', '+47'],
  NP: ['Nepal', '+977'],
  NZ: ['New Zealand', '+64'],
  OM: ['Oman', '+968'],
  PH: ['Philippines', '+63'],
  PK: ['Pakistan', '+92'],
  PL: ['Poland', '+48'],
  PT: ['Portugal', '+351'],
  QA: ['Qatar', '+974'],
  RO: ['Romania', '+40'],
  SA: ['Saudi Arabia', '+966'],
  SE: ['Sweden', '+46'],
  SG: ['Singapore', '+65'],
  TH: ['Thailand', '+66'],
  TR: ['Turkey', '+90'],
  ZA: ['South Africa', '+27'],
};

const PINNED = ['IN', 'US'];

/** Flag emoji for an ISO-2 code: two regional-indicator letters. */
const flagOf = (/** @type {string} */ iso) =>
  String.fromCodePoint(...[...iso].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));

/** @type {Country[]} */
const all = Object.entries(SOURCE).map(([iso, [name, dial]]) => ({ iso, name, dial, flag: flagOf(iso) }));

/** @type {Country[]} */
export const COUNTRIES = [
  ...PINNED.map((iso) => /** @type {Country} */ (all.find((c) => c.iso === iso))),
  ...all.filter((c) => !PINNED.includes(c.iso)).sort((a, b) => a.name.localeCompare(b.name)),
];

/**
 * @param {string | null | undefined} iso
 * @returns {Country | undefined}
 */
export const countryByIso = (iso) => COUNTRIES.find((c) => c.iso === iso);

/**
 * The country an E.164 number ("+91...") belongs to, by its dial code, or undefined when the number
 * has no leading + or an unknown code. Several countries can share a code (US and Canada are +1):
 * the preferred one wins when it matches, else the first listed.
 * @param {string | null | undefined} phone
 * @param {string | null | undefined} [preferredIso]
 * @returns {Country | undefined}
 */
export function countryForPhone(phone, preferredIso) {
  const text = String(phone ?? '').trim();
  if (!text.startsWith('+')) return undefined;
  const digits = text.slice(1).replace(/\D/g, '');
  const matches = COUNTRIES.filter((c) => digits.startsWith(c.dial.slice(1)));
  if (matches.length === 0) return undefined;
  const longest = Math.max(...matches.map((c) => c.dial.length));
  const best = matches.filter((c) => c.dial.length === longest);
  return best.find((c) => c.iso === preferredIso) ?? best[0];
}
